import { IButtonStyles, IconButton, Stack, useTheme } from '@fluentui/react';
import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from '@fluentui/react';
import { FilterElement } from '../Filters';
import useResizeObserver from 'use-resize-observer';

const SCROLL_AMOUNT = 180;

interface ScrollableFilterBarProps {
  filters: FilterElement[];
}

const useStyles = makeStyles(() => ({
  hideScrollbar: {
    overflowX: 'auto',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    selectors: {
      '::-webkit-scrollbar': { display: 'none' },
    },
  },
  scrollButton: {},
}));

export const ScrollableFilterBar: React.FC<ScrollableFilterBarProps> = ({
  filters,
}) => {
  const styles = useStyles();
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scrollState, setScrollState] = useState({
    atStart: true,
    atEnd: false,
  });
  const [needsScroll, setNeedsScroll] = useState(false);

  useResizeObserver<HTMLDivElement>({
    ref: containerRef,
    onResize: () => handleScroll(),
  });

  const scrollButtonStyle: IButtonStyles = {
    rootHovered: {
      backgroundColor: theme.palette.neutralQuaternaryAlt,
    },
    iconDisabled: {
      color: theme.palette.neutralTertiaryAlt,
    },
  };

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;

    setScrollState({
      atStart: el.scrollLeft <= 0,
      atEnd: el.scrollLeft + el.clientWidth >= el.scrollWidth,
    });
    setNeedsScroll(
      !!(
        containerRef.current &&
        containerRef.current.scrollWidth > containerRef.current.clientWidth
      )
    );
  };

  useEffect(() => {
    handleScroll();
  }, [filters]);

  const handleNext = () =>
    containerRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' });
  const handlePrev = () =>
    containerRef.current?.scrollBy({
      left: -SCROLL_AMOUNT,
      behavior: 'smooth',
    });

  return (
    <Stack
      horizontal
      verticalAlign="center"
      style={{
        minWidth: 250,
        flex: '0 1 auto',
        boxSizing: 'border-box',
        marginLeft: 8,
      }}
    >
      <IconButton
        style={{ display: needsScroll ? 'unset' : 'none' }}
        disabled={scrollState.atStart}
        iconProps={{ iconName: 'ChevronLeftMed' }}
        onClick={handlePrev}
        styles={scrollButtonStyle}
      />
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className={styles.hideScrollbar}
      >
        <div
          style={{
            display: 'flex',
            gap: '2px',
            alignItems: 'center',
            height: 50,
          }}
        >
          {filters.map((f) => (
            <div key={f.props.filterKey} style={{ flexShrink: 0 }}>
              {f}
            </div>
          ))}
        </div>
      </div>

      <IconButton
        style={{
          display: needsScroll ? 'unset' : 'none',
        }}
        disabled={scrollState.atEnd}
        iconProps={{ iconName: 'ChevronRightMed' }}
        onClick={handleNext}
        styles={scrollButtonStyle}
      />
    </Stack>
  );
};
